import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, MenuItem, CircularProgress, Alert, Tabs, Tab, InputAdornment, Button,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import ThemeBackground from '../../components/Layout/ThemeBackground';
import { format, parseISO, isValid } from 'date-fns';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend
} from 'recharts';

const QUEUE_LABELS: Record<string, string> = {
  idle: 'Idle clients', outstanding: 'Outstanding payments', offboard: 'Awaiting offboard',
  contract: 'Nearing contract end', unassigned: 'Unassigned', stalled: 'Stalled intake',
  requests: 'Stale payment requests', activity: 'Counsellor activity'
};

interface IdleClient {
  _id: string;
  ClientRef?: string;
  name: string;
  status: string;
  lastSessionDate: string | null;
  daysSinceSession: number | null;
  counsellorName?: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active', assigned: 'Assigned', not_started: 'Not Started',
  pending: 'Pending', pending_archive: 'Pending Archive', completed: 'Completed'
};
const statusLabel = (s?: string) => STATUS_LABELS[s || ''] || s || 'Unknown';
const ELIGIBILITY_LABELS: Record<string, string> = {
  eligible: 'Eligible', awaiting_client: 'Awaiting client',
  planned_break: 'Planned break', clinician_review: 'Clinician review',
  do_not_contact: 'Do not contact'
};

interface CounsellorActivity {
  _id: string;
  name: string;
  email: string;
  lastLogin: string | null;
  lastNoteAt: string | null;
  activeClientCount: number;
}

interface SimpleRow {
  _id: string;
  ClientRef?: string;
  name: string;
  status?: string;
  since?: string;
  outstandingBalance?: number;
  allocated?: number;
  attended?: number;
  amount?: number;
  missed?: number;
  requestPending?: boolean;
  requestedAt?: string | null;
  action?: { itemId: string; assignee: string; followUpAt?: string; note?: string } | null;
}

interface Report {
  days: number;
  summary: {
    clientsInPipeline: number; idleClients: number; activeCounsellors: number;
    debtors: number; awaitingOffboard: number; nearingContractEnd: number; unassigned: number;
    expiredRequests: number;
  };
  idleClients: IdleClient[];
  counsellorActivity: CounsellorActivity[];
  outstandingPayments: SimpleRow[];
  awaitingOffboard: SimpleRow[];
  nearingContractEnd: SimpleRow[];
  unassigned: SimpleRow[];
  stalledIntake: SimpleRow[];
  missedAppointments: SimpleRow[];
  expiredPaymentRequests: SimpleRow[];
  recencyDistribution: { bucket: string; clients: number }[];
  assignees?: { _id: string; name: string }[];
  openActions?: {
    itemId: string; queue: string; subjectId: string; subjectName: string;
    subjectType: string; assignee: string; note?: string; followUpAt?: string; createdAt: string;
  }[];
  trend?: { date: string; summary: Record<string, number>; medianDays?: Record<string, number | null> }[];
  intakeFunnel?: {
    stages: { key: string; label: string; count: number; conversion: number | null }[];
    medianDays: Record<string, number | null>;
    bySource: { source: string; enquiries: number; reachedFirstSession: number; conversion: number | null }[];
  };
}

const fmtDate = (d?: string | null) => {
  if (!d) return 'Never';
  const p = parseISO(d);
  return isValid(p) ? format(p, 'dd MMM yyyy') : 'Unknown';
};

const cellSx = { color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.1)' };
const cardSx = {
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 3,
  background: 'rgba(255,255,255,0.04)',
  p: 2.5
};

const UtilisationReport: React.FC = () => {
  const [report, setReport] = useState<Report | null>(null);
  const [days, setDays] = useState(30);
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [activeQueue, setActiveQueue] = useState('idle');
  const [assignTarget, setAssignTarget] = useState<{ queue: string; subjectId: string; subjectName: string; subjectType: 'client' | 'counsellor' } | null>(null);
  const [assignTo, setAssignTo] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [autoWorkflow, setAutoWorkflow] = useState<'stalled_intake' | 'idle_client'>('stalled_intake');

  const loadAutomation = async () => {
    try {
      const [c, a] = await Promise.all([
        api.get(`/api/reports/automation/candidates?days=${days}&workflow=${autoWorkflow}`),
        api.get('/api/reports/automation-actions')
      ]);
      setCandidates(c.data);
      setActions(a.data);
    } catch { /* surfaced via page error */ }
  };
  useEffect(() => { if (tab === 3) loadAutomation(); }, [tab, days, autoWorkflow]);

  const createActions = async () => {
    try {
      await api.post('/api/reports/automation-actions', { clientIds: Array.from(selected), workflow: autoWorkflow });
      setSelected(new Set());
      loadAutomation();
    } catch { /* surfaced via reload */ }
  };

  const actOnAction = async (id: string, op: 'approve' | 'suppress') => {
    try {
      await api.post(`/api/reports/automation-actions/${id}/${op}`, {});
      loadAutomation();
    } catch { /* surfaced via reload */ }
  };

  const submitAssign = async () => {
    if (!assignTarget || !assignTo) return;
    try {
      await api.post('/api/reports/action-items', { ...assignTarget, assignedTo: assignTo, note: assignNote || undefined });
      setAssignTarget(null);
      setAssignTo('');
      setAssignNote('');
      setRefreshTick(t => t + 1);
    } catch { /* surfaced via reload state */ }
  };

  const setEligibility = async (clientId: string, state: string) => {
    try {
      await api.patch(`/api/clients/${clientId}/contact-eligibility`, { state });
      setRefreshTick(t => t + 1);
    } catch { /* surfaced via reload state */ }
  };

  const resolveItem = async (itemId: string) => {
    try {
      await api.post(`/api/reports/action-items/${itemId}/resolve`);
      setRefreshTick(t => t + 1);
    } catch { /* surfaced via reload state */ }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/reports/utilisation?days=${days}`);
        setReport(res.data);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days, refreshTick]);

  const match = (name: string) =>
    !search || name.toLowerCase().includes(search.toLowerCase());

  const filteredIdle = useMemo(
    () => (report?.idleClients || []).filter(c => match(c.name + (c.ClientRef || ''))),
    [report, search]
  );

  const statCards = report ? [
    { label: 'Clients in pipeline', value: report.summary.clientsInPipeline, color: '#818cf8' },
    { label: `Idle ≥${report.days}d`, value: report.summary.idleClients, color: '#f56565' },
    { label: 'Owing money', value: report.summary.debtors, color: '#ed8936' },
    { label: 'Awaiting offboard', value: report.summary.awaitingOffboard, color: '#b794f4' },
    { label: 'Near contract end', value: report.summary.nearingContractEnd, color: '#63b3ed' },
    { label: 'Unassigned', value: report.summary.unassigned, color: '#cbd5e0' },
    { label: 'Stale pay requests', value: report.summary.expiredRequests, color: '#fc8181' },
    { label: 'Active counsellors', value: report.summary.activeCounsellors, color: '#68d391' }
  ] : [];

  return (
    <ThemeBackground variant="full">
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
        {/* Header - consistent with MTDDashboard/ArchivedRecords */}
        <div className="glass-card-light mb-6">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div className="flex flex-col space-y-4">
                <div className="flex space-x-4">
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      backgroundColor: '#2196f3',
                      '&:hover': { backgroundColor: '#1976d2' }
                    }}
                    onClick={() => navigate('/')}
                  >
                    Back to Home
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '120px',
                      backgroundColor: '#9c27b0',
                      '&:hover': { backgroundColor: '#7b1fa2' }
                    }}
                    onClick={() => setRefreshTick(t => t + 1)}
                  >
                    Refresh
                  </Button>
                </div>
              </div>
              <div className="text-right">
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  className="theme-text-primary"
                  sx={{ fontFamily: 'Roboto', fontWeight: 800, fontSize: '2.5rem', letterSpacing: '0.5px' }}
                >
                  Utilisation Report
                </Typography>
                <Typography
                  variant="subtitle1"
                  className="theme-text-secondary"
                  sx={{ fontWeight: 500, fontSize: '1.1rem' }}
                >
                  How well the CRM is being used
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <TextField
            select
            size="small"
            label="Idle / stale for"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{
              minWidth: 160,
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
              '& .MuiSelect-select': { color: 'white' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' }
            }}
            SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: 'rgba(30,41,59,0.98)', '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.85)' } } } } }}
          >
            {[14, 30, 60, 90, 180].map(d => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
          </TextField>
        </Box>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {report && (
          <>
            {/* Stat cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5, mb: 3 }}>
              {statCards.map(s => (
                <Box key={s.label} sx={{ border: '1px solid rgba(255,255,255,0.14)', borderRadius: 2, background: 'rgba(255,255,255,0.04)', p: 1.5 }}>
                  <Typography sx={{ color: s.color, fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.1 }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Tabs */}
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                mb: 3,
                borderBottom: '1px solid rgba(255,255,255,0.12)',
                '& .MuiTab-root': { color: 'rgba(255,255,255,0.95)', textTransform: 'none', fontWeight: 600 },
                '& .Mui-selected': { color: '#a5b4fc' },
                '& .MuiTabs-indicator': { backgroundColor: '#818cf8' }
              }}
            >
              <Tab label="Overview" />
              <Tab label="Action Queues" />
              <Tab label="Counsellor Activity" />
              <Tab label="Automation" />
            </Tabs>

            {/* Overview tab */}
            {tab === 0 && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                <Box sx={cardSx}>
                  <Typography className="theme-text-primary" sx={{ fontWeight: 700, mb: 2 }}>
                    Time since last session (whole pipeline)
                  </Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={report.recencyDistribution || []} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="bucket" stroke="rgba(255,255,255,0.6)" fontSize={12} />
                      <YAxis stroke="rgba(255,255,255,0.6)" fontSize={12} allowDecimals={false} />
                      <Tooltip cursor={{ fill: 'rgba(255,255,255,0.08)' }} contentStyle={{ background: 'rgba(30,41,59,0.98)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#fff' }} />
                      <Bar dataKey="clients" radius={[4, 4, 0, 0]}>
                        {(report.recencyDistribution || []).map((b, i) => (
                          <Cell key={i} fill={['#68d391', '#f6ad55', '#ed8936', '#f56565', '#c53030', '#9b2c2c'][i] || '#818cf8'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={cardSx}>
                  <Typography className="theme-text-primary" sx={{ fontWeight: 700, mb: 2 }}>
                    Missed appointments by counsellor
                  </Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={report.missedAppointments || []} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" fontSize={11} interval={0} angle={-25} textAnchor="end" height={60} />
                      <YAxis stroke="rgba(255,255,255,0.6)" fontSize={12} allowDecimals={false} />
                      <Tooltip cursor={{ fill: 'rgba(255,255,255,0.08)' }} contentStyle={{ background: 'rgba(30,41,59,0.98)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#fff' }} />
                      <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }} />
                      <Bar dataKey="missed" name="Missed (not attended)" fill="#f56565" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ ...cardSx, gridColumn: { xs: 'auto', md: '1 / -1' } }}>
                  <Typography className="theme-text-primary" sx={{ fontWeight: 700, mb: 2 }}>
                    Intake funnel
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>{['Stage', 'Clients', '% of enquiries', 'Median days to next'].map(h => <TableCell key={h} sx={{ ...cellSx, fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.intakeFunnel?.stages || []).map((s, i) => {
                        const gapKeys = ['enquiryToAssessment', 'assessmentToContract', 'contractToAllocation', 'allocationToFirstSession'];
                        return (
                          <TableRow key={s.key}>
                            <TableCell sx={cellSx}>{s.label}</TableCell>
                            <TableCell sx={cellSx}>{s.count}</TableCell>
                            <TableCell sx={cellSx}>{s.conversion != null ? `${s.conversion}%` : '—'}</TableCell>
                            <TableCell sx={cellSx}>{i < gapKeys.length && report.intakeFunnel?.medianDays?.[gapKeys[i]] != null ? `${report.intakeFunnel.medianDays[gapKeys[i]]}d` : '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {(report.intakeFunnel?.bySource?.length ?? 0) > 0 && (
                    <>
                      <Typography className="theme-text-primary" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                        Source → first session
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>{['Source', 'Enquiries', 'Reached session', 'Conversion'].map(h => <TableCell key={h} sx={{ ...cellSx, fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                        </TableHead>
                        <TableBody>
                          {(report.intakeFunnel?.bySource || []).map(s => (
                            <TableRow key={s.source}>
                              <TableCell sx={cellSx}>{s.source}</TableCell>
                              <TableCell sx={cellSx}>{s.enquiries}</TableCell>
                              <TableCell sx={cellSx}>{s.reachedFirstSession}</TableCell>
                              <TableCell sx={cellSx}>{s.conversion != null ? `${s.conversion}%` : '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </Box>
                {(report.trend?.length ?? 0) > 1 && (
                  <Box sx={{ ...cardSx, gridColumn: { xs: 'auto', md: '1 / -1' } }}>
                    <Typography className="theme-text-primary" sx={{ fontWeight: 700, mb: 2 }}>
                      Weekly trend
                    </Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={(report.trend || []).map(t => ({ date: t.date.slice(5), pipeline: t.summary.clientsInPipeline, idle: t.summary.idleClients, debtors: t.summary.debtors, offboard: t.summary.awaitingOffboard }))} margin={{ top: 5, right: 15, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="date" stroke="rgba(255,255,255,0.6)" fontSize={11} />
                        <YAxis stroke="rgba(255,255,255,0.6)" fontSize={12} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: 'rgba(30,41,59,0.98)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#fff' }} />
                        <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }} />
                        <Line type="monotone" dataKey="pipeline" name="In pipeline" stroke="#818cf8" dot={false} />
                        <Line type="monotone" dataKey="idle" name="Idle" stroke="#f56565" dot={false} />
                        <Line type="monotone" dataKey="debtors" name="Owing money" stroke="#ed8936" dot={false} />
                        <Line type="monotone" dataKey="offboard" name="Awaiting offboard" stroke="#b794f4" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            )}
            {tab === 1 && (
              <Box>
                {(report.openActions?.length ?? 0) > 0 && (
                  <Box sx={{ ...cardSx, mb: 2.5, borderColor: 'rgba(165,180,252,0.4)' }}>
                    <Typography className="theme-text-primary" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Open assignments ({report.openActions!.length})
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>{['Queue', 'Subject', 'Assigned to', 'Follow-up', 'Note', ''].map(h => <TableCell key={h} sx={{ ...cellSx, fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                      </TableHead>
                      <TableBody>
                        {report.openActions!.map(a => (
                          <TableRow
                            key={a.itemId}
                            onClick={() => navigate(a.subjectType === 'counsellor' ? `/counsellors/${a.subjectId}` : `/client-details/${a.subjectId}`)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
                          >
                            <TableCell sx={cellSx}>{QUEUE_LABELS[a.queue] || a.queue}</TableCell>
                            <TableCell sx={cellSx}>{a.subjectName}</TableCell>
                            <TableCell sx={{ ...cellSx, color: '#a5b4fc' }}>{a.assignee}</TableCell>
                            <TableCell sx={{ ...cellSx, ...(a.followUpAt && new Date(a.followUpAt) < new Date() ? { color: '#f56565', fontWeight: 700 } : {}) }}>{a.followUpAt ? fmtDate(a.followUpAt) : '—'}</TableCell>
                            <TableCell sx={cellSx}>{a.note || '—'}</TableCell>
                            <TableCell sx={cellSx} onClick={(e) => e.stopPropagation()}>
                              <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, py: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }} onClick={() => resolveItem(a.itemId)}>Done</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                <TextField
                  size="small"
                  placeholder="Filter by client or counsellor name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'rgba(255,255,255,0.5)' }} /></InputAdornment> }}
                  sx={{
                    mb: 2.5, minWidth: 320,
                    '& .MuiInput-input': { color: 'white' },
                    '& .MuiOutlinedInput-input': { color: 'white' },
                    '& input': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' }
                  }}
                />

                {(() => {
                  const sections = [
                    { key: 'idle', title: `Clients idle ${report.days}+ days`, rows: filteredIdle, cols: ['Client', 'Counsellor', 'Status', 'Last Session', 'Days Idle'], cell: (r: any) => [r.name + (r.ClientRef ? ` (${r.ClientRef})` : ''), r.counsellorName || 'Not Assigned', statusLabel(r.status), fmtDate(r.lastSessionDate), r.daysSinceSession == null ? 'never' : String(r.daysSinceSession)], accent: '#f56565' },
                    { key: 'outstanding', title: 'Outstanding payments', rows: report.outstandingPayments.filter(r => match(r.name + (r.ClientRef || ''))), cols: ['Client', 'Outstanding', 'Request Issued'], cell: (r: SimpleRow) => [r.name + (r.ClientRef ? ` (${r.ClientRef})` : ''), `£${((r.outstandingBalance || 0) / 100).toFixed(2)}`, r.requestPending ? `Yes – ${fmtDate(r.requestedAt)}` : 'No'], accent: '#ed8936' },
                    { key: 'offboard', title: 'Awaiting offboard', rows: report.awaitingOffboard.filter(r => match(r.name + (r.ClientRef || ''))), cols: ['Client', 'Since'], cell: (r: SimpleRow) => [r.name + (r.ClientRef ? ` (${r.ClientRef})` : ''), fmtDate(r.since)], accent: '#b794f4' },
                    { key: 'contract', title: 'Nearing contract end', rows: report.nearingContractEnd.filter(r => match(r.name + (r.ClientRef || ''))), cols: ['Client', 'Used / Funded'], cell: (r: SimpleRow) => [r.name + (r.ClientRef ? ` (${r.ClientRef})` : ''), `${r.attended} / ${r.allocated}`], accent: '#63b3ed' },
                    { key: 'unassigned', title: 'Unassigned clients', rows: report.unassigned.filter(r => match(r.name + (r.ClientRef || ''))), cols: ['Client', 'Status'], cell: (r: SimpleRow) => [r.name + (r.ClientRef ? ` (${r.ClientRef})` : ''), statusLabel(r.status)], accent: '#cbd5e0' },
                    { key: 'stalled', title: `Stalled intake (${report.days}+ days)`, rows: report.stalledIntake.filter(r => match(r.name + (r.ClientRef || ''))), cols: ['Client', 'Status', 'Since'], cell: (r: SimpleRow) => [r.name + (r.ClientRef ? ` (${r.ClientRef})` : ''), statusLabel(r.status), fmtDate(r.since)], accent: '#f6ad55' },
                    { key: 'requests', title: `Stale payment requests (${report.days}+ days)`, rows: report.expiredPaymentRequests.filter(r => match(r.name + (r.ClientRef || ''))), cols: ['Client', 'Amount', 'Requested'], cell: (r: SimpleRow) => [r.name, `£${(r.amount || 0).toFixed(2)}`, fmtDate(r.since)], accent: '#fc8181' }
                  ];
                  const active = sections.find(s => s.key === activeQueue) || sections[0];
                  return (
                    <>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5, mb: 3 }}>
                        {sections.map(s => (
                          <Box
                            key={s.key}
                            onClick={() => setActiveQueue(s.key)}
                            sx={{
                              border: `1px solid ${activeQueue === s.key ? s.accent : 'rgba(255,255,255,0.14)'}`,
                              borderRadius: 2,
                              background: activeQueue === s.key ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
                              p: 1.5,
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              '&:hover': { borderColor: s.accent, background: 'rgba(255,255,255,0.07)' }
                            }}
                          >
                            <Typography sx={{ color: s.accent, fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.1 }}>{s.rows.length}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>{s.title}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={cardSx}>
                        <Typography className="theme-text-primary" sx={{ fontWeight: 700, mb: 2 }}>
                          {active.title}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>{[...active.cols, ...(active.rows.some((r: any) => 'contactEligibility' in r) ? ['Eligibility'] : []), 'Owner'].map(h => <TableCell key={h} sx={{ ...cellSx, fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                          </TableHead>
                          <TableBody>
                            {active.rows.map((r: any) => (
                              <TableRow
                                key={r._id}
                                onClick={() => navigate(`/client-details/${r._id}`)}
                                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
                              >
                                {active.cell(r).map((v: string, i: number) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
                                {'contactEligibility' in r && (
                                  <TableCell sx={cellSx} onClick={(e) => e.stopPropagation()}>
                                    <TextField
                                      select size="small"
                                      value={r.contactEligibility || 'eligible'}
                                      onChange={(e) => setEligibility(r._id, e.target.value)}
                                      sx={{ minWidth: 150, '& .MuiSelect-select': { color: 'white', py: 0.5, fontSize: '0.8rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                                      SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: 'rgba(30,41,59,0.98)', '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.85)' } } } } }}
                                    >
                                      {[
                                        ['eligible', 'Eligible'], ['awaiting_client', 'Awaiting client'],
                                        ['planned_break', 'Planned break'], ['clinician_review', 'Clinician review'],
                                        ['do_not_contact', 'Do not contact']
                                      ].map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                                    </TextField>
                                  </TableCell>
                                )}
                                <TableCell sx={cellSx} onClick={(e) => e.stopPropagation()}>
                                  {r.action ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" sx={{ color: '#a5b4fc' }}>{r.action.assignee}</Typography>
                                      <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, py: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }} onClick={() => resolveItem(r.action!.itemId)}>Done</Button>
                                    </Box>
                                  ) : (
                                    <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, py: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }} onClick={() => setAssignTarget({ queue: active.key, subjectId: r._id, subjectName: r.name, subjectType: 'client' })}>Assign</Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                            {active.rows.length === 0 && <TableRow><TableCell sx={cellSx} colSpan={active.cols.length + 2}>None.</TableCell></TableRow>}
                          </TableBody>
                        </Table>
                      </Box>
                    </>
                  );
                })()}
              </Box>
            )}

            {/* Counsellor Activity tab */}
            {tab === 2 && (
              <Box sx={cardSx}>
                <Typography className="theme-text-primary" sx={{ fontWeight: 700, mb: 2 }}>
                  Counsellor activity
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>{['Counsellor', 'Last Login', 'Last Client Note', 'Clients', 'Owner'].map(h => <TableCell key={h} sx={{ ...cellSx, fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                  </TableHead>
                  <TableBody>
                    {report.counsellorActivity.filter(c => match(c.name)).map(c => (
                      <TableRow
                        key={c._id}
                        onClick={() => navigate(`/counsellors/${c._id}`)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
                      >
                        <TableCell sx={cellSx}>{c.name}</TableCell>
                        <TableCell sx={cellSx}>{fmtDate(c.lastLogin)}</TableCell>
                        <TableCell sx={cellSx}>{fmtDate(c.lastNoteAt)}</TableCell>
                        <TableCell sx={cellSx}>{c.activeClientCount}</TableCell>
                        <TableCell sx={cellSx} onClick={(e) => e.stopPropagation()}>
                          {(c as any).action ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" sx={{ color: '#a5b4fc' }}>{(c as any).action.assignee}</Typography>
                              <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, py: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }} onClick={() => resolveItem((c as any).action.itemId)}>Done</Button>
                            </Box>
                          ) : (
                            <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, py: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }} onClick={() => setAssignTarget({ queue: 'activity', subjectId: c._id, subjectName: c.name, subjectType: 'counsellor' })}>Assign</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {/* Automation tab - stalled-intake reminders, approval-required */}
            {tab === 3 && (
              <Box>
                <Box sx={{ ...cardSx, mb: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography className="theme-text-primary" sx={{ fontWeight: 700 }}>
                        {autoWorkflow === 'stalled_intake' ? 'Stalled-intake' : 'Idle-client'} candidates ({candidates.length})
                      </Typography>
                      <TextField
                        select size="small" value={autoWorkflow}
                        onChange={(e) => { setAutoWorkflow(e.target.value as any); setSelected(new Set()); }}
                        sx={{ minWidth: 150, '& .MuiSelect-select': { color: 'white', py: 0.5, fontSize: '0.8rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                        SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: 'rgba(30,41,59,0.98)', '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.85)' } } } } }}
                      >
                        <MenuItem value="stalled_intake">Stalled intake</MenuItem>
                        <MenuItem value="idle_client">Idle clients</MenuItem>
                      </TextField>
                    </Box>
                    <Button
                      variant="contained" size="small" disabled={selected.size === 0}
                      onClick={createActions}
                      sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: '#6366f1', '&:hover': { backgroundColor: '#4f46e5' } }}
                    >
                      Create {selected.size} follow-up{selected.size === 1 ? '' : 's'}
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>{['', 'Client', 'Status', 'Since', 'Eligibility', ''].map(h => <TableCell key={h} sx={{ ...cellSx, fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                      {candidates.map(c => (
                        <TableRow key={c._id}>
                          <TableCell sx={cellSx}>
                            <input
                              type="checkbox"
                              disabled={!c.contactable}
                              checked={selected.has(c._id)}
                              onChange={(e) => {
                                const s = new Set(selected);
                                e.target.checked ? s.add(c._id) : s.delete(c._id);
                                setSelected(s);
                              }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>{c.name}{c.ClientRef ? ` (${c.ClientRef})` : ''}</TableCell>
                          <TableCell sx={cellSx}>{statusLabel(c.status)}</TableCell>
                          <TableCell sx={cellSx}>{fmtDate(c.since)}</TableCell>
                          <TableCell sx={cellSx}>{ELIGIBILITY_LABELS[c.eligibility] || c.eligibility}</TableCell>
                          <TableCell sx={cellSx}>{c.contactable ? '' : (c.blockedReason || '')}</TableCell>
                        </TableRow>
                      ))}
                      {candidates.length === 0 && <TableRow><TableCell sx={cellSx} colSpan={6}>No stalled-intake candidates.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </Box>
                <Box sx={cardSx}>
                  <Typography className="theme-text-primary" sx={{ fontWeight: 700, mb: 2 }}>
                    Automation actions ({actions.filter(a => a.status === 'pending_approval').length} pending)
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>{['Client', 'Workflow', 'Status', 'Reason', ''].map(h => <TableCell key={h} sx={{ ...cellSx, fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                      {actions.map(a => (
                        <TableRow key={a._id}>
                          <TableCell sx={cellSx}>{a.clientName}</TableCell>
                          <TableCell sx={cellSx}>{a.workflow}</TableCell>
                          <TableCell sx={cellSx}>{a.status}</TableCell>
                          <TableCell sx={cellSx}>{a.suppressedReason || a.reason || '—'}</TableCell>
                          <TableCell sx={cellSx}>
                            {a.status === 'pending_approval' && (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" variant="contained" sx={{ minWidth: 0, px: 1, py: 0, fontSize: '0.7rem', textTransform: 'none', backgroundColor: '#22c55e' }} onClick={() => actOnAction(a._id, 'approve')}>Approve</Button>
                                <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, py: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }} onClick={() => actOnAction(a._id, 'suppress')}>Suppress</Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {actions.length === 0 && <TableRow><TableCell sx={cellSx} colSpan={5}>No actions yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </>
        )}
        {/* Assign owner dialog */}
        <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} PaperProps={{ sx: { bgcolor: 'rgba(30,41,59,0.98)', border: '1px solid rgba(255,255,255,0.15)' } }}>
          <DialogTitle sx={{ color: 'white' }}>Assign {assignTarget?.subjectName}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 320, pt: 1 }}>
            <TextField
              select size="small" label="Assign to" value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              sx={{ '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiSelect-select': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
              SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: 'rgba(30,41,59,0.98)', '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.85)' } } } } }}
            >
              {(report?.assignees || []).map(a => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
            </TextField>
            <TextField
              size="small" label="Note (optional)" value={assignNote} multiline rows={2}
              onChange={(e) => setAssignNote(e.target.value)}
              sx={{ '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiInputBase-root': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignTarget(null)} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" disabled={!assignTo} onClick={submitAssign} sx={{ textTransform: 'none' }}>Assign</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeBackground>
  );
};

export default UtilisationReport;
